import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { createRoot } from 'react-dom/client';
import { OrgProvider } from './app/OrgProvider';
import { AuthProvider } from './auth/AuthProvider';
import { initAnalytics } from './lib/analytics';
import { ApiError } from './lib/api';
import { router } from './router';
import './styles.css';

/**
 * Note the absence of <StrictMode>.
 *
 * StrictMode double-invokes effects in development, which for an ordinary app
 * is a useful bug detector and for this one is a hazard: the recorder acquires
 * MediaStreams, MediaRecorders and AudioContexts in effects, and tearing those
 * down and rebuilding them behind the user's back means a second screen-share
 * prompt and a dead capture. The recorder's own lifecycle is written to be
 * idempotent; the development-only double-mount is not worth the noise.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Retrying a 401/403/404 just delays the real message.
        if (error instanceof ApiError && error.status < 500 && error.status !== 429) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

initAnalytics();

const container = document.getElementById('root');
if (!container) throw new Error('index.html is missing its #root element.');

createRoot(container).render(
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <OrgProvider>
        <RouterProvider router={router} />
      </OrgProvider>
    </AuthProvider>
  </QueryClientProvider>,
);
